import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanyErpStatus } from "../../components/CompanyErpStatus";
import { Link, useLocation, useParams } from "react-router-dom";
import { DiscountMatrixCard } from "../../components/admin/DiscountMatrixCard";
import {
  adminApi,
  type Company,
  type CompanyAddress,
  type CompanyAddressBody,
  type OtkupAssignment,
  type SalesCoverage,
  type SalesCoverageEntry,
  type User,
} from "../../api/admin";
import { ordersApi, type Order } from "../../api/orders";
import { forecastsApi, periodLabel, type Forecast } from "../../api/forecasts";
import { contractsApi, type Contract } from "../../api/contracts";
import { complaintsApi, type Complaint } from "../../api/complaints";
import { useAuth } from "../../auth/AuthContext";
import type { ApiError } from "../../api/client";
import { OrderStatusBadge } from "../../components/OrderStatusBadge";
import { AddressFormFields } from "../../components/AddressFormFields";
import { Select } from "../../components/Select";
import { CompanyContactsCard } from "../../components/admin/CompanyContactsCard";
import { GoodsDocumentSetCard } from "../../components/admin/GoodsDocumentSetCard";
import { CommunicationSection } from "../../components/mail/CommunicationSection";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { countryName, flagEmoji } from "../../i18n/countries";
import { localeFlag, localeLabel } from "../../i18n/LocaleSwitcher";
import type { SupportedLocale } from "../../i18n";
import { CustomerDashboardView } from "./CustomerDashboardView";
import { CreditCheckCard } from "../../components/CreditCheckCard";
import { CompanyContracts } from "../../components/CompanyContracts";
import { procurementContractsApi } from "../../api/procurementContracts";
import { deliveriesApi } from "../../api/deliveries";
import { supplierFinanceApi } from "../../api/supplierFinance";
import { formatDate, formatMoneyCode, intlLocale } from "../../lib/format";

type CompanyTab = "customerView" | "manage";

/**
 * Customer detail — Sales lands here from /admin/companies and gets the full
 * per-customer view: metadata, sales coverage (direct + via market/country
 * scope), service rep, and rollups of orders / forecasts / contracts that
 * link out to the dedicated pages. Visual treatment matches the rest of the
 * portal (page-head, card class, var colours, KPI strip).
 */
export default function AdminCompanyDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  /** Stanje navigacije nosi mesto sa kog se došlo; `window.location` to ne bi znao. */
  const putanja = useLocation();
  const toast = useToast();
  const confirm = useConfirm();
  const { state: authState, hasPermission } = useAuth();
  // The documents that go with the goods are Bankom's own arrangement with the customer; its users do not see it.
  const internal = authState.status === "authenticated" && authState.user.internal;

  const [company, setCompany] = useState<Company | null>(null);
  const [coverage, setCoverage] = useState<SalesCoverage | null>(null);
  const [salesUsers, setSalesUsers] = useState<User[]>([]);
  const [serviceUsers, setServiceUsers] = useState<User[]>([]);
  const [pickedSales, setPickedSales] = useState("");
  const [pickedPrimary, setPickedPrimary] = useState(false);
  const [otkupAccountId, setOtkupAccountId] = useState<string>("");
  const [savingOtkupRep, setSavingService] = useState(false);
  /** Brojke otkupne glave. `null` znači „još se čita", a ne nula — nula je tvrdnja. */
  const [otkup, setOtkup] = useState<{
    ugovora: number | null;
    aktivnih: number | null;
    isporuka: number | null;
    poslednja: string | null;
    saldo: number | null;
    dospelo: number | null;
    valuta: string | null;
    /** Šta se nije pročitalo. Neuspeh nije nula: nula tvrdi da nema, a ovde se ne zna. */
    neuspelo: string[];
  }>({
    ugovora: null, aktivnih: null, isporuka: null, poslednja: null,
    saldo: null, dospelo: null, valuta: null, neuspelo: [],
  });
  const [otkupAssignments, setOtkupAssignments] = useState<OtkupAssignment[] | null>(null);
  const [pickedOtkup, setPickedOtkup] = useState("");
  const [pickedOtkupPrimary, setPickedOtkupPrimary] = useState(false);
  const [settlementMode, setSettlementMode] =
    useState<"SELF_BILLED" | "SUPPLIER_INVOICES">("SUPPLIER_INVOICES");
  const [paymentTerm, setPaymentTerm] = useState("");
  const [savingSettlement, setSavingSettlement] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [complaints, setComplaints] = useState<Complaint[] | null>(null);
  const [forecasts, setForecasts] = useState<Forecast[] | null>(null);
  const [contracts, setContracts] = useState<Contract[] | null>(null);
  // "Customer view" (default) previews the company's-eye dashboard; "Manage"
  // holds every admin section this page had before.
  const [tab, setTab] = useState<CompanyTab>("customerView");

  const isSysAdmin = hasPermission("section:market_settings:full");
  const canReadAssignments = hasPermission("section:market_settings:read");
  const canSetSettlement = hasPermission("section:settlement_rules:write") && hasPermission("section:companies:write");
  const canEditCompanies = hasPermission("companies:edit");
  const canCreateForecast = hasPermission("forecasts:manage");
  const canCreateContract = hasPermission("contracts:create");

  async function load() {
    if (!id) return;
    try {
      const [c, cov] = await Promise.all([
        adminApi.getCompany(id),
        canReadAssignments ? adminApi.listCompanySalesCoverage(id) : Promise.resolve(null),
      ]);
      setCompany(c);
      setCoverage(cov);
      setOtkupAccountId(c.otkupAccountId ?? "");
      setSettlementMode(c.settlementMode ?? "SUPPLIER_INVOICES");
      setPaymentTerm(c.paymentTermDays == null ? "" : String(c.paymentTermDays));
      await loadOtkupAssignments(c);
    } catch (err) {
      setError((err as ApiError).message);
    }
  }

  // Who works the otkup side of a company is the administrator's to see and to
  // change — sales edits the same page but does not read otkup — and it only
  // means something for a company that sells or may sell to Bankom: a supplier,
  // or a company with no kind yet. Best-effort: a failure leaves the card empty,
  // never the page.
  async function loadOtkupAssignments(c: Company) {
    if (!id || !isSysAdmin || c.kind === "CUSTOMER") return;
    try {
      setOtkupAssignments(await adminApi.listCompanyOtkup(id));
    } catch {
      setOtkupAssignments([]);
    }
  }

  async function loadRollups() {
    if (!id) return;
    // Panel na stranici firme, ne spisak: uzima najnovije, ne sve.
    if (hasPermission("section:orders:read")) void ordersApi.list({ companyId: id, size: 100 })
        .then((r) => setOrders(r.content))
        .catch(() => setOrders([]));
    if (hasPermission("section:complaint_submission:read") || hasPermission("section:complaint_processing:read")) void complaintsApi.list({ companyId: id }).then(setComplaints).catch(() => setComplaints([]));
    if (hasPermission("section:forecasts:read")) void forecastsApi.list({ companyId: id }).then(setForecasts).catch(() => setForecasts([]));
    if (hasPermission("section:sales_contracts:read")) void contractsApi.list({ companyId: id }).then(setContracts).catch(() => setContracts([]));
    void loadOtkupRollups();
  }

  /**
   * Brojke otkupne glave. Traži se samo zbir, ne spisak: strana od jednog reda vraća `totalElements`,
   * pa se registar ne povlači da bi se prebrojao.
   *
   * Svaka se traži zasebno i neuspeh jedne ne ruši ostale — kartica sme da ostane bez jedne brojke,
   * ali ne sme da ostane bez strane.
   */
  async function loadOtkupRollups() {
    if (!id) return;
    if (hasPermission("section:procurement_contracts:read")) {
      void Promise.all([
        procurementContractsApi.list({ companyId: id }, 0, 1),
        procurementContractsApi.list({ companyId: id, status: "ACTIVE" }, 0, 1),
      ])
        .then(([sve, aktivni]) =>
          setOtkup((o) => ({ ...o, ugovora: sve.totalElements, aktivnih: aktivni.totalElements })),
        )
        .catch(() => setOtkup((o) => ({ ...o, neuspelo: [...o.neuspelo, "ugovori"] })));
    }
    if (hasPermission("section:delivery_intake:read")) {
      // Jedan red je dovoljan i za zbir i za poslednju: spisak je ređan od najnovije.
      void deliveriesApi
        .list({ companyId: id }, 0, 1)
        .then((p) =>
          setOtkup((o) => ({
            ...o,
            isporuka: p.totalElements,
            poslednja: p.content[0]?.receivedOn ?? p.content[0]?.announcedDate ?? null,
          })),
        )
        .catch(() => setOtkup((o) => ({ ...o, neuspelo: [...o.neuspelo, "isporuke"] })));
    }
    if (hasPermission("section:supplier_finance:read")) {
      void supplierFinanceApi
        .company(id)
        .then((d) =>
          setOtkup((o) => ({
            ...o,
            saldo: d.account.balanceTotal,
            dospelo: d.account.overdueTotal,
            valuta: d.account.currency,
          })),
        )
        .catch(() => setOtkup((o) => ({ ...o, neuspelo: [...o.neuspelo, "finansije"] })));
    }
  }

  useEffect(() => {
    void load();
    void loadRollups();
    if (isSysAdmin) void adminApi
      .listUsers(0, 200)
      .then((p) => {
        const all = p.content.filter(Boolean);
        setSalesUsers(all.filter((u) => u.roles.includes("SALES") || u.roles.includes("SALES_DIRECTOR")));
        setServiceUsers(all.filter((u) => u.internal && u.roles.includes("OTKUP")));
      })
      .catch(() => {
        setSalesUsers([]);
        setServiceUsers([]);
      });
  }, [id, isSysAdmin]);

  async function handleSaveSettlement(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSavingSettlement(true);
    try {
      // Empty means "not agreed": the liability then falls due the day it arises.
      // An absent field would leave the stored term alone, so the blank is sent
      // as an explicit clear.
      const updated = await adminApi.updateCompany(id, paymentTerm.trim() === ""
        ? { settlementMode, clearPaymentTermDays: true }
        : { settlementMode, paymentTermDays: Number(paymentTerm) });
      setCompany(updated);
      toast.success(t("admin.companyDetail.toast.settlementSaved", "Settlement settings updated."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setSavingSettlement(false);
    }
  }

  async function handleSaveServiceRep(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSavingService(true);
    try {
      const updated = await adminApi.updateCompany(id, otkupAccountId
        ? { otkupAccountId }
        : { clearOtkupAccountId: true });
      setCompany(updated);
      setOtkupAccountId(updated.otkupAccountId ?? "");
      // Saving the rep also assigns them, so the assignment card must follow.
      await loadOtkupAssignments(updated);
      toast.success(t("admin.companyDetail.toast.otkupRepSaved", "Otkup officer updated."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setSavingService(false);
    }
  }

  async function handleAssign(e: FormEvent) {
    e.preventDefault();
    if (!id || !pickedSales) return;
    try {
      await adminApi.assignSales(id, { salesUserId: pickedSales, primary: pickedPrimary });
      setPickedSales("");
      setPickedPrimary(false);
      await load();
      toast.success(t("admin.companyDetail.toast.assigned", "Sales rep assigned."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function handleRemove(assignmentId: string) {
    if (!id) return;
    if (!(await confirm({ message: t("admin.companyDetail.confirmRemove", "Remove this sales rep?"), danger: true }))) return;
    try {
      await adminApi.removeSales(id, assignmentId);
      await load();
      toast.success(t("admin.companyDetail.toast.removed", "Sales rep removed."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function handleAssignOtkup(e: FormEvent) {
    e.preventDefault();
    if (!id || !pickedOtkup || !company) return;
    try {
      await adminApi.assignOtkup(id, { otkupUserId: pickedOtkup, primary: pickedOtkupPrimary });
      setPickedOtkup("");
      setPickedOtkupPrimary(false);
      await loadOtkupAssignments(company);
      toast.success(t("admin.companyDetail.toast.otkupAssigned", "Otkup officer assigned."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function handleRemoveOtkup(otkupUserId: string) {
    if (!id || !company) return;
    if (!(await confirm({ message: t("admin.companyDetail.otkupAssignments.confirmRemove", "Remove this otkup assignment?"), danger: true }))) return;
    try {
      await adminApi.removeOtkup(id, otkupUserId);
      await loadOtkupAssignments(company);
      toast.success(t("admin.companyDetail.toast.otkupRemoved", "Otkup assignment removed."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  const allCoverage: SalesCoverageEntry[] = useMemo(() => {
    if (!coverage) return [];
    return [...coverage.direct, ...coverage.viaScope];
  }, [coverage]);

  const candidateSales = salesUsers.filter(
    (u) => !allCoverage.some((e) => e.userId === u.id && e.origin === "DIRECT"),
  );
  const candidateOtkup = serviceUsers.filter(
    (u) => !(otkupAssignments ?? []).some((a) => a.otkupUserId === u.id),
  );

  if (error) {
    return (
      <div style={{ padding: 16, background: "var(--err-bg)", color: "var(--err)", borderRadius: 8 }}>
        {error}
      </div>
    );
  }
  if (!company) {
    return <p style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</p>;
  }

  const locale = i18n.resolvedLanguage ?? "en";
  const country = company.country ?? null;
  const countryLabel = country ? countryName(country, locale) : null;
  const sediste = glavnaAdresa(company);
  const statusLabel = t(`admin.companies.status.${company.status}`, company.status);
  // Clean subtitle: full localized names, no codes/abbreviations, no empty
  // segments (a company with no country simply drops that segment).
  const subParts = [
    countryLabel,
    company.vatNumber,
    localeLabel(company.defaultLocale as SupportedLocale),
    statusLabel,
  ].filter((s): s is string => !!s && s.trim() !== "");

  // Kind-aware sections (hard rule: sales ≠ otkup). A CUSTOMER sees only the
  // sales context; a SUPPLIER (kooperant) only the otkup context; BOTH sees
  // both, and so does a company with no kind yet.
  const kind = company.kind;
  const supplierOnly = kind === "SUPPLIER";
  /** Odakle se došlo. Spisak to pošalje kao stanje navigacije; direktan link ga nema. */
  const stanjeNazad = (putanja.state as { nazad?: unknown } | null)?.nazad;
  /**
   * Bez stanja — direktan link, osvežena strana — pada na spisak otvoren na tabu kome firma
   * pripada. Goli `/admin/companies` otvara kupce, pa je čist kooperant dobijao natpis
   * „Dobavljači" na linku koji vodi među kupce; natpis mora da imenuje ono kuda stvarno vodi.
   */
  const nazadNaSpisak =
    typeof stanjeNazad === "string"
      ? stanjeNazad
      : kind === "SUPPLIER"
        ? "/admin/companies?tab=suppliers"
        : "/admin/companies";
  /**
   * Natpis mrvice imenuje ono KUDA vodi, pa se čita iz same adrese kad je ima.
   *
   * Firma koja je i kupac i dobavljač dolazi sa oba taba; po samoj vrsti firme natpis bi joj uvek
   * pisao „Customers" i poslao čoveka natrag sa pogrešnim imenom na linku koji ga vraća na tab
   * dobavljača.
   */
  const tabSpiska = new URLSearchParams(nazadNaSpisak.split("?")[1] ?? "").get("tab");
  /** Natpis mrvice imenuje tab na koji link vraća, sva tri. */
  const natpisSpiska =
    tabSpiska === "suppliers"
      ? t("admin.companyDetail.breadcrumbSuppliers", "Dobavljači")
      : tabSpiska === "all"
        ? t("admin.companies.tab.all", "All companies")
        : t("admin.companyDetail.breadcrumb", "Customers");
  const showSales = !supplierOnly; // CUSTOMER, BOTH, or null
  // A company with no kind yet shows both sides: a new co-operative is a supplier
  // only after the ERP books its first purchase, and its otkup officer has to be
  // set before that.
  const showOtkup = kind !== "CUSTOMER";
  const effectiveTab: CompanyTab = supplierOnly ? "manage" : tab;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* `page-head` poravnava po dnu, što je dobro kad su oba dela niska. Kartica boniteta je
          viša od naslova, pa je po dnu gurala naslov naniže — ime firme je padalo ispod ocene.
          Ovde se poravnava po vrhu, da oba krenu iz istog reda. */}
      <div className="page-head" style={{ alignItems: "flex-start" }}>
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            {/* Mrvica vraća tamo odakle se došlo, sa stranom i filterom: spisak od trinaest hiljada
                firmi bez toga vraća čoveka na prvu stranu, pa se do firme sa sedme strane dolazi
                ponovo. Kad stanja nema — direktan link, osvežena strana — pada na goli spisak.
                Natpis imenuje tab na koji vraća — pročitan iz same adrese, jer firma koja je i
                kupac i dobavljač dolazi sa oba taba. Bez adrese pada na vrstu firme. */}
            <span>
              <Link to={nazadNaSpisak} style={crumbLink}>
                {natpisSpiska}
              </Link>
            </span>
            <span style={crumbCurrent}>{company.name}</span>
          </nav>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)", display: "flex", alignItems: "center", gap: 10 }}>
            {country ? <span style={{ fontSize: 24 }}>{flagEmoji(country)}</span> : null}
            {company.name}
          </h1>
          <div className="sub">{subParts.join(" · ")}</div>
        </div>
        {/* Bonitet stoji uz ime firme, u desnom delu zaglavlja koji je do sada stajao prazan.
            Tu i pripada: menja odluku pre nego što se bilo šta ugovori. */}
        {hasPermission("section:credit_check:read") && <CreditCheckCard companyId={company.id} />}
      </div>

      {/* The customer-dashboard preview is a sales-side concept — hidden for
          suppliers, who only ever see the (otkup) Manage view. */}
      {showSales && (
        <div role="tablist" aria-label={t("company.tabs.aria", "Company view")} style={tabBar}>
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === "customerView"}
            onClick={() => setTab("customerView")}
            style={tabBtn(effectiveTab === "customerView")}
          >
            {t("company.tabs.customerView", "Customer view")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === "manage"}
            onClick={() => setTab("manage")}
            style={tabBtn(effectiveTab === "manage")}
          >
            {t("company.tabs.manage", "Manage")}
          </button>
        </div>
      )}

      {effectiveTab === "customerView" && id && (
        <CustomerDashboardView companyId={id} companyName={company.name} />
      )}

      {effectiveTab === "manage" && (
        <>
      {showSales && (
      <div style={kpiGrid}>
        {hasPermission("section:orders:read") && <KpiTile
          label={t("admin.companyDetail.kpi.orders", "Orders")}
          value={orders?.length ?? "—"}
          accent="var(--info)" bg="var(--info-bg)"
          hint={
            orders && orders.length > 0
              ? t("admin.companyDetail.kpi.awaitingReview", { count: orders.filter((o) => o.status === "SUBMITTED").length, defaultValue: "{{count}} awaiting review" })
              : t("admin.companyDetail.kpi.nothingYet", "Nothing yet")
          }
        />}
        {hasPermission("section:forecasts:read") && <KpiTile
          label={t("admin.companyDetail.kpi.forecasts", "Forecasts")}
          value={forecasts?.length ?? "—"}
          accent="var(--tone-violet)" bg="var(--tone-violet-bg)"
          hint={
            forecasts && forecasts.length > 0
              ? t("admin.companyDetail.kpi.forecastsBreakdown", {
                  draft: forecasts.filter((f) => f.status === "DRAFT").length,
                  submitted: forecasts.filter((f) => f.status === "SUBMITTED").length,
                  defaultValue: "{{draft}} draft, {{submitted}} submitted",
                })
              : t("admin.companyDetail.kpi.noForecasts", "No forecasts yet")
          }
        />}
        {hasPermission("section:sales_contracts:read") && <KpiTile
          label={t("admin.companyDetail.kpi.contracts", "Contracts")}
          value={contracts?.length ?? "—"}
          accent="var(--ok)" bg="var(--ok-bg)"
          hint={
            contracts && contracts.length > 0
              ? t("admin.companyDetail.kpi.contractsActive", { count: contracts.filter((c) => c.status === "ACTIVE").length, defaultValue: "{{count}} active" })
              : t("admin.companyDetail.kpi.noContracts", "No contracts on file")
          }
        />}
        {hasPermission("section:complaint_processing:read") && <KpiTile
          label={t("admin.companyDetail.kpi.complaints", "Complaints")}
          value={complaints?.length ?? "—"}
          accent="var(--err)" bg="var(--err-bg)"
          hint={
            complaints && complaints.length > 0
              ? t("admin.companyDetail.kpi.complaintsOpen", {
                  count: complaints.filter((c) => c.status === "SUBMITTED" || c.status === "IN_REVIEW").length,
                  defaultValue: "{{count}} open",
                })
              : t("admin.companyDetail.kpi.noComplaints", "No complaints")
          }
        />}
      </div>
      )}

      {/* Otkupna glava. Dok je nije bilo, čist dobavljač je imao prazan vrh kartice: KPI traka je
          cela stajala pod `showSales`, pa je kupac dobijao četiri pločice a kooperant nijednu —
          kartica je bila kupčeva sa rupama umesto svoja. Brojke su otkupne i vode na otkupne
          ekrane, koji firmu sada primaju iz adrese. */}
      {showOtkup && (
      <div style={kpiGrid}>
        {hasPermission("section:procurement_contracts:read") && (
          <Link to={`/admin/procurement/contracts?companyId=${id}`} style={{ textDecoration: "none" }}>
            <KpiTile
              label={t("admin.companyDetail.kpi.procurementContracts", "Otkupni ugovori")}
              value={otkup.ugovora ?? "—"}
              accent="var(--brand-primary)"
              bg="var(--brand-primary-50)"
              hint={
                otkup.neuspelo.includes("ugovori")
                  ? t("admin.companyDetail.kpi.readFailed", "nije pročitano")
                  : otkup.ugovora === null
                  ? t("admin.companyDetail.kpi.loading", "Čitam…")
                  : otkup.ugovora === 0
                    ? t("admin.companyDetail.kpi.noProcurementContracts", "Nema otkupnih ugovora")
                    : t("admin.companyDetail.kpi.procurementActive", {
                        count: otkup.aktivnih ?? 0,
                        defaultValue: "{{count}} aktivnih",
                      })
              }
            />
          </Link>
        )}
        {hasPermission("section:delivery_intake:read") && (
          <Link to={`/procurement/deliveries?companyId=${id}`} style={{ textDecoration: "none" }}>
            <KpiTile
              label={t("admin.companyDetail.kpi.deliveries", "Isporuke")}
              value={otkup.isporuka ?? "—"}
              accent="var(--ok)"
              bg="var(--ok-bg)"
              hint={
                otkup.neuspelo.includes("isporuke")
                  ? t("admin.companyDetail.kpi.readFailed", "nije pročitano")
                  : otkup.isporuka === null
                  ? t("admin.companyDetail.kpi.loading", "Čitam…")
                  : otkup.poslednja
                    ? t("admin.companyDetail.kpi.lastDelivery", {
                        date: formatDate(otkup.poslednja, i18n.resolvedLanguage),
                        defaultValue: "poslednja {{date}}",
                      })
                    : t("admin.companyDetail.kpi.noDeliveries", "Nema isporuka")
              }
            />
          </Link>
        )}
        {hasPermission("section:supplier_finance:read") && (
          <Link to={`/procurement/finance/companies/${id}`} style={{ textDecoration: "none" }}>
            <KpiTile
              label={t("admin.companyDetail.kpi.supplierFinance", "Finansije dobavljača")}
              value={
                otkup.saldo === null
                  ? "—"
                  : formatMoneyCode(otkup.saldo, otkup.valuta ?? "RSD", i18n.resolvedLanguage)
              }
              accent="var(--warn)"
              bg="var(--warn-bg)"
              hint={
                otkup.neuspelo.includes("finansije")
                  ? t("admin.companyDetail.kpi.readFailed", "nije pročitano")
                  : otkup.saldo === null
                  ? t("admin.companyDetail.kpi.loading", "Čitam…")
                  : otkup.dospelo && otkup.dospelo > 0
                    ? t("admin.companyDetail.kpi.overdue", {
                        amount: formatMoneyCode(otkup.dospelo, otkup.valuta ?? "RSD", i18n.resolvedLanguage),
                        defaultValue: "dospelo {{amount}}",
                      })
                    : t("admin.companyDetail.kpi.nothingOverdue", "Ništa nije dospelo")
              }
            />
          </Link>
        )}
      </div>
      )}

      <div style={twoCol}>
        <section className="card">
          <header className="card-h">
            <h3>{t("admin.companyDetail.section.details", "Details")}</h3>
          </header>
          <div className="card-b">
            <DetailGrid
              rows={[
                [t("admin.companyDetail.field.vat", "VAT"), company.vatNumber ?? "—"],
                ...(company.parentVatNumber
                  ? [[t("admin.companyDetail.field.parentVat", "Parent company VAT"), company.parentVatNumber]] as [string, string][]
                  : []),
                [t("admin.companyDetail.field.address", "Address"), sediste || "—"],
                [t("admin.companyDetail.field.country", "Country"), country ? `${flagEmoji(country)} ${countryLabel}` : "—"],
                [t("admin.companyDetail.field.defaultLocale", "Default locale"), `${localeFlag(company.defaultLocale as SupportedLocale)} ${localeLabel(company.defaultLocale as SupportedLocale)}`],
                [t("admin.companyDetail.field.status", "Status"), statusLabel],
                [t("admin.companyDetail.field.created", "Created"), new Date(company.createdAt).toLocaleString(i18n.language)],
                ...(company.erpId ? [[t("admin.companyDetail.field.erpId", "ERP ID"), company.erpId]] as [string, string][] : []),
              ]}
            />
            <CompanyErpStatus companyId={company.id} />
          </div>
        </section>

        {showOtkup && canSetSettlement && (
        <section className="card">
          <header className="card-h">
            <h3>{t("admin.companyDetail.section.settlement", "Settlement and payment")}</h3>
          </header>
          <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>
              {t("admin.companyDetail.settlement.help",
                  "Who issues the document for a purchase, and how long Bankom has to pay. Both are Bankom's practice, not a portal assumption.")}
            </p>
            <form onSubmit={handleSaveSettlement} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 240 }}>
                <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
                  {t("admin.companyDetail.settlement.mode", "Settlement mode")}
                </span>
                <Select<string>
                  value={settlementMode}
                  onChange={(v) => setSettlementMode(v as "SELF_BILLED" | "SUPPLIER_INVOICES")}
                  ariaLabel={t("admin.companyDetail.settlement.mode", "Settlement mode")}
                  options={[
                    {
                      value: "SUPPLIER_INVOICES",
                      label: t("admin.companyDetail.settlement.supplierInvoices",
                          "The co-operative invoices Bankom"),
                    },
                    {
                      value: "SELF_BILLED",
                      label: t("admin.companyDetail.settlement.selfBilled",
                          "Bankom issues the settlement itself"),
                    },
                  ]}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 160 }}>
                <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
                  {t("admin.companyDetail.settlement.paymentTerm", "Payment term (days)")}
                </span>
                <input
                  type="number"
                  min="0"
                  className="input"
                  value={paymentTerm}
                  onChange={(e) => setPaymentTerm(e.target.value)}
                  placeholder={t("admin.companyDetail.settlement.notAgreed", "not agreed")}
                />
              </label>
              <button type="submit" className="btn primary" disabled={savingSettlement}>
                {t("common.save", "Save")}
              </button>
            </form>
            {settlementMode === "SELF_BILLED" && paymentTerm.trim() === "" && (
              <p style={{ color: "var(--ink-3)", fontSize: 12, margin: 0 }}>
                {t("admin.companyDetail.settlement.dueSameDay",
                    "With no agreed term, each settlement falls due on the day it is made.")}
              </p>
            )}
          </div>
        </section>
        )}

        {showOtkup && (
        <section className="card">
          <header className="card-h">
            <h3>{t("admin.companyDetail.section.otkupRep", "Otkup officer")}</h3>
          </header>
          <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>
              {t("admin.companyDetail.otkupRep.help",
                  "Internal Otkup staff assigned to this company.")}
            </p>
            {isSysAdmin ? (
              <form onSubmit={handleSaveServiceRep} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Select<string>
                  value={otkupAccountId}
                  onChange={setOtkupAccountId}
                  style={{ flex: 1, minWidth: 200 }}
                  options={[
                    { value: "", label: t("admin.companyDetail.otkupRep.none", "— Unassigned —") },
                    ...serviceUsers.map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName} (${u.email})` })),
                  ]}
                />
                <button
                  type="submit"
                  className="btn primary"
                  disabled={savingOtkupRep || otkupAccountId === (company.otkupAccountId ?? "")}
                >
                  {savingOtkupRep ? "…" : t("admin.companyDetail.action.save", "Save")}
                </button>
              </form>
            ) : (
              <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 14 }}>
                {company.otkupAccountName ?? t("admin.companyDetail.otkupRep.none", "— Unassigned —")}
              </div>
            )}
          </div>
        </section>
        )}
      </div>

      {showOtkup && isSysAdmin && (
      <section className="card">
        <header className="card-h">
          <h3>{t("admin.companyDetail.section.otkupAssignments", "Assigned otkup officers")}</h3>
          <span className="sub">
            {otkupAssignments
              ? t("admin.companyDetail.otkupAssignments.count", { count: otkupAssignments.length, defaultValue: "Otkup officers on this co-operative: {{count}}" })
              : ""}
          </span>
        </header>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>
            {t("admin.companyDetail.otkupAssignments.help",
                "Otkup officers who act for this co-operative: announcements, contracts, settlements. Every otkup officer can see every co-operative; the assignment decides who may act. Saving the otkup officer above adds them here automatically.")}
          </p>
          {otkupAssignments === null ? (
            <Muted>{t("common.loading", "Loading…")}</Muted>
          ) : otkupAssignments.length === 0 ? (
            <EmptyHint icon="👤" text={t("admin.companyDetail.otkupAssignments.empty", "No otkup officer is assigned to this co-operative yet.")} />
          ) : (
            <ul style={list}>
              {otkupAssignments.map((a) => (
                <OtkupAssignmentRow key={a.id} entry={a} onRemove={() => void handleRemoveOtkup(a.otkupUserId)} />
              ))}
            </ul>
          )}

          <form onSubmit={handleAssignOtkup} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
            <Select<string>
              value={pickedOtkup}
              onChange={setPickedOtkup}
              ariaLabel={t("admin.companyDetail.otkupAssignments.pickPlaceholder", "Pick an otkup officer to assign…")}
              style={{ flex: 1, minWidth: 240 }}
              options={[
                { value: "", label: t("admin.companyDetail.otkupAssignments.pickPlaceholder", "Pick an otkup officer to assign…") },
                ...candidateOtkup.map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName} (${u.email})` })),
              ]}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-2)" }}>
              <input type="checkbox" checked={pickedOtkupPrimary} onChange={(e) => setPickedOtkupPrimary(e.target.checked)} />
              {t("admin.companyDetail.primaryLabel", "Primary")}
            </label>
            <button type="submit" className="btn primary" disabled={!pickedOtkup}>
              {t("admin.companyDetail.action.assign", "Assign")}
            </button>
          </form>
        </div>
      </section>
      )}

      {id && <CompanyContactsCard companyId={id} canEdit={canEditCompanies} />}

      {/* Correspondence with this customer — same component as the lead timeline.
          The modal prefills the recipient from the company's primary contact. */}
      {id && showSales && (
        <CommunicationSection
          companyId={id}
          defaultSubject={t("mail.send.companySubject", { company: company.name, defaultValue: "Bankom — {{company}}" })}
        />
      )}

      {id && <AddressBookCard companyId={id} canEdit={canEditCompanies} company={company} />}

      {id && internal && showSales && <GoodsDocumentSetCard companyId={id} canEdit={canEditCompanies} />}

      {showSales && (
      <>
      <section className="card">
        <header className="card-h">
          <h3>{t("admin.companyDetail.section.salesAssignments", "Sales coverage")}</h3>
          <span className="sub">
            {coverage
              ? t("admin.companyDetail.coverage.repCount", { count: allCoverage.length, defaultValue: "Sales reps on this customer: {{count}}" })
              : ""}
          </span>
        </header>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {coverage === null ? (
            <Muted>{t("common.loading", "Loading…")}</Muted>
          ) : allCoverage.length === 0 ? (
            <EmptyHint icon="👤" text={t("admin.companyDetail.coverage.empty", "Nobody is assigned to this customer yet.")} />
          ) : (
            <ul style={list}>
              {coverage.direct.map((e) => (
                <CoverageRow key={`d-${e.id}`} entry={e} canRemove={isSysAdmin && !!e.id} onRemove={() => e.id && handleRemove(e.id)} />
              ))}
              {coverage.viaScope.map((e) => (
                <CoverageRow key={`s-${e.userId}`} entry={e} canRemove={false} onRemove={() => {}} />
              ))}
            </ul>
          )}

          {isSysAdmin && (
            <form onSubmit={handleAssign} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
              <Select<string>
                value={pickedSales}
                onChange={setPickedSales}
                style={{ flex: 1, minWidth: 240 }}
                options={[
                  { value: "", label: t("admin.companyDetail.pickSalesPlaceholder", "Pick a sales rep to add directly…") },
                  ...candidateSales.map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName} (${u.email})` })),
                ]}
              />
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-2)" }}>
                <input type="checkbox" checked={pickedPrimary} onChange={(e) => setPickedPrimary(e.target.checked)} />
                {t("admin.companyDetail.primaryLabel", "Primary")}
              </label>
              <button type="submit" className="btn primary" disabled={!pickedSales}>
                {t("admin.companyDetail.action.assign", "Assign")}
              </button>
            </form>
          )}
        </div>
      </section>

      {hasPermission("section:orders:read") && <section className="card">
        <header className="card-h">
          <h3>{t("admin.companyDetail.section.orders", "Orders")}</h3>
          <Link to={`/orders?companyId=${id}`} style={actionLink}>{t("admin.companyDetail.viewAll", "View all →")}</Link>
        </header>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {orders === null ? (
            <Muted>{t("common.loading", "Loading…")}</Muted>
          ) : orders.length === 0 ? (
            <EmptyHint icon="📦" text={t("admin.companyDetail.orders.empty", "No orders for this customer yet.")} />
          ) : (
            <>
              <RollupSummary
                total={orders.length}
                groups={[
                  { label: t("admin.companyDetail.rollup.submitted", "Submitted"), count: orders.filter((o) => o.status === "SUBMITTED").length, accent: "var(--info)" },
                  { label: t("admin.companyDetail.rollup.inReview", "In review"),  count: orders.filter((o) => o.status === "IN_REVIEW").length,  accent: "var(--warn)" },
                  { label: t("admin.companyDetail.rollup.approved", "Approved"),   count: orders.filter((o) => o.status === "APPROVED").length, accent: "var(--tone-cyan)" },
                  { label: t("admin.companyDetail.rollup.shipped", "Shipped"),    count: orders.filter((o) => o.status === "SHIPPED" || o.status === "INVOICED").length, accent: "var(--ok)" },
                ]}
              />
              <ul style={list}>
                {orders.slice(0, 5).map((o) => (
                  <li key={o.id} style={row}>
                    <Link to={`/orders/${o.id}`} style={{ flex: 1, color: "var(--ink)", textDecoration: "none", minWidth: 0 }}>
                      <code style={mono}>{o.orderNumber}</code>
                      <span style={{ color: "var(--ink-3)", marginLeft: 8, fontSize: 12 }}>
                        {/* Datum nastanka, pa tek onda rok isporuke: rok postoji na manjini
                            porudžbina, a bez datuma se red ne može smestiti u vreme. */}
                        {fmtDate(o.submittedAt ?? o.createdAt, i18n.language)}
                        {" · "}
                        {t("admin.companyDetail.linesCount", { count: o.lines.length, defaultValue: "{{count}} lines" })}
                        {o.requestedDeliveryDate ? ` · ${t("admin.companyDetail.due", { date: fmtDate(o.requestedDeliveryDate, i18n.language), defaultValue: "due {{date}}" })}` : ""}
                      </span>
                    </Link>
                    <OrderStatusBadge status={o.status} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>}

      {hasPermission("section:forecasts:read") && <section className="card">
        <header className="card-h">
          <h3>{t("admin.companyDetail.section.forecasts", "Forecasts")}</h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {canCreateForecast && (
              <Link to={`/forecasts?companyId=${id}&new=1`} className="btn primary sm" style={{ textDecoration: "none" }}>{t("admin.companyDetail.newForecast", "+ New forecast")}</Link>
            )}
            <Link to={`/forecasts?companyId=${id}`} style={actionLink}>{t("admin.companyDetail.viewAll", "View all →")}</Link>
          </div>
        </header>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {forecasts === null ? (
            <Muted>{t("common.loading", "Loading…")}</Muted>
          ) : forecasts.length === 0 ? (
            <EmptyHint
              icon="📈"
              text={t("admin.companyDetail.forecasts.empty", "No forecasts entered yet for this customer.")}
              hint={canCreateForecast ? t("admin.companyDetail.forecasts.emptyHint", "Use \"+ New forecast\" to record forward demand.") : undefined}
            />
          ) : (
            <>
              <RollupSummary
                total={forecasts.length}
                groups={[
                  { label: t("admin.companyDetail.rollup.draft", "Draft"),     count: forecasts.filter((f) => f.status === "DRAFT").length,     accent: "var(--ink-3)" },
                  { label: t("admin.companyDetail.rollup.submitted", "Submitted"), count: forecasts.filter((f) => f.status === "SUBMITTED").length, accent: "var(--info)" },
                ]}
              />
              <ul style={list}>
                {forecasts.slice(0, 5).map((f) => (
                  <li key={f.id} style={row}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <b style={{ fontSize: 13, color: "var(--ink)" }}>{periodLabel(f)}</b>
                      <span style={{ color: "var(--ink-3)", marginLeft: 8, fontSize: 12 }}>
                        {t("admin.companyDetail.linesCount", { count: f.forecastLines.length, defaultValue: "{{count}} lines" })}
                      </span>
                    </span>
                    <span style={neutralChip}>{t(`forecasts.status.${f.status}`, f.status)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>}

      {hasPermission("section:sales_contracts:read") && <section className="card">
        <header className="card-h">
          <h3>{t("admin.companyDetail.section.contracts", "Contracts")}</h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {canCreateContract && (
              <Link to={`/admin/contracts?companyId=${id}`} className="btn primary sm" style={{ textDecoration: "none" }}>{t("admin.companyDetail.newContract", "+ New contract")}</Link>
            )}
            <Link to={`/admin/contracts?companyId=${id}`} style={actionLink}>{t("admin.companyDetail.viewAll", "View all →")}</Link>
          </div>
        </header>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {contracts === null ? (
            <Muted>{t("common.loading", "Loading…")}</Muted>
          ) : contracts.length === 0 ? (
            <EmptyHint
              icon="📄"
              text={t("admin.companyDetail.contracts.empty", "No contracts on file for this customer.")}
              hint={canCreateContract ? t("admin.companyDetail.contracts.emptyHint", "Use \"+ New contract\" to start one.") : undefined}
            />
          ) : (
            <>
              <RollupSummary
                total={contracts.length}
                groups={[
                  { label: t("admin.companyDetail.rollup.active", "Active"),   count: contracts.filter((c) => c.status === "ACTIVE").length,    accent: "var(--ok)" },
                  { label: t("admin.companyDetail.rollup.sent", "Sent"),     count: contracts.filter((c) => c.status === "SENT_TO_CUSTOMER" || c.status === "CUSTOMER_VIEWED").length, accent: "var(--info)" },
                  { label: t("admin.companyDetail.rollup.signed", "Signed"),   count: contracts.filter((c) => c.status === "SIGNED").length,    accent: "var(--tone-violet)" },
                  { label: t("admin.companyDetail.rollup.expired", "Expired"),  count: contracts.filter((c) => c.status === "EXPIRED").length,   accent: "var(--err)" },
                ]}
              />
              {/* Kartice umesto golog reda: red je nosio samo broj, naslov i datum, a ugovor se
                  otvara zbog količine, ispunjenosti i preostalog roka. */}
              <CompanyContracts ugovori={contracts.slice(0, 5)} />
            </>
          )}
        </div>
      </section>}

      {id && hasPermission("section:discounts:read") && <DiscountMatrixCard companyId={id} />}
      </>
      )}
        </>
      )}
    </div>
  );
}

/** Sedište firme u jednom redu: ulica, dodatak, poštanski broj i grad, bez praznih delova. */
function glavnaAdresa(c: Company): string {
  const ulica = [c.addressLine1, c.addressLine2].filter(Boolean).join(", ");
  const mesto = [c.postalCode, c.city].filter(Boolean).join(" ");
  return [ulica, mesto].filter(Boolean).join(", ");
}

/**
 * Company address book card (delivery or billing). Internal-only —
 * lives on the admin company page, gated by companies:edit. Lists each entry
 * with purpose tags + default markers and lets staff add / edit / soft-delete /
 * promote a per-purpose default. Customers never see this; they pick + inline-add
 * on the order itself.
 */
function AddressBookCard({
  companyId,
  canEdit,
  company,
}: {
  companyId: string;
  canEdit: boolean;
  company: Company;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const [addresses, setAddresses] = useState<CompanyAddress[] | null>(null);
  const [editing, setEditing] = useState<CompanyAddress | "new" | null>(null);

  async function load() {
    try {
      setAddresses(await adminApi.listCompanyAddresses(companyId));
    } catch {
      setAddresses([]);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  async function handleSave(body: CompanyAddressBody) {
    if (editing === "new") {
      await adminApi.createCompanyAddress(companyId, body);
      toast.success(t("company.addresses.toast.added", "Address added."));
    } else if (editing) {
      await adminApi.updateCompanyAddress(companyId, editing.id, body);
      toast.success(t("company.addresses.toast.saved", "Address saved."));
    }
    setEditing(null);
    await load();
  }

  async function handleDelete(a: CompanyAddress) {
    if (!(await confirm({ message: t("company.addresses.confirmDelete", "Delete this address?"), danger: true }))) return;
    try {
      await adminApi.deleteCompanyAddress(companyId, a.id);
      toast.success(t("company.addresses.toast.deleted", "Address deleted."));
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function handleSetDefault(a: CompanyAddress, purpose: "delivery" | "billing") {
    try {
      await adminApi.setCompanyAddressDefault(companyId, a.id, purpose);
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  return (
    <section className="card">
      <header className="card-h">
        <h3>{t("company.addresses.title", "Addresses")}</h3>
        {canEdit && (
          <button type="button" className="btn primary sm" onClick={() => setEditing("new")}>
            {t("company.addresses.add", "+ Add address")}
          </button>
        )}
      </header>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {addresses === null ? (
          <Muted>{t("common.loading", "Loading…")}</Muted>
        ) : addresses.length === 0 ? (
          // Sedište firme se stvarno koristi kao podrazumevana adresa, pa se i prikazuje.
          // Rečenica da se „koristi glavna adresa" bez te adrese ne govori ništa.
          glavnaAdresa(company) ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                {t("company.addresses.mainAddress", "Sedište firme — koristi se kao podrazumevana adresa")}
              </span>
              <strong>{company.name}</strong>
              <span>{glavnaAdresa(company)}</span>
            </div>
          ) : (
            <EmptyHint icon="📍" text={t("company.addresses.emptyNoMain", "Nema sačuvanih adresa, a ni firma nema upisano sedište.")} />
          )
        ) : (
          <ul style={list}>
            {addresses.map((a) => (
              <AddressRow
                key={a.id}
                address={a}
                canEdit={canEdit}
                onEdit={() => setEditing(a)}
                onDelete={() => void handleDelete(a)}
                onSetDefault={(p) => void handleSetDefault(a, p)}
              />
            ))}
          </ul>
        )}
      </div>
      {canEdit && editing && (
        <AddressFormDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </section>
  );
}

function AddressRow({
  address: a, canEdit, onEdit, onDelete, onSetDefault,
}: {
  address: CompanyAddress;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onSetDefault: (purpose: "delivery" | "billing") => void;
}) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const countryLabel = a.country ? countryName(a.country, i18n.resolvedLanguage ?? "en") : null;
  const cityLine = [a.postalCode, a.city].filter(Boolean).join(" ");
  return (
    <li style={{ ...row, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <b style={{ fontSize: 13, color: "var(--ink)" }}>
            {a.label || a.recipientName || a.addressLine1}
          </b>
          {a.usableForDelivery && (
            <span style={purposeChip("var(--info)", "var(--info-bg)")}>
              {t("company.addresses.delivery", "Delivery")}
              {a.defaultDelivery && <span title={t("company.addresses.default", "Default")}> ★</span>}
            </span>
          )}
          {a.usableForBilling && (
            <span style={purposeChip("var(--tone-violet)", "var(--tone-violet-bg)")}>
              {t("company.addresses.billing", "Billing")}
              {a.defaultBilling && <span title={t("company.addresses.default", "Default")}> ★</span>}
            </span>
          )}
        </div>
        {a.recipientName && a.label && (
          <span style={{ fontSize: 12, color: "var(--ink-2)" }}>{a.recipientName}</span>
        )}
        <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
          {a.addressLine1}
          {a.addressLine2 ? `, ${a.addressLine2}` : ""}
          {cityLine ? `, ${cityLine}` : ""}
          {countryLabel ? `, ${countryLabel}` : ""}
        </span>
        {a.vatNumber && (
          <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>
            {t("company.addresses.vat", "VAT")}: {a.vatNumber}
          </span>
        )}
      </div>
      {canEdit && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onEdit} style={textBtn("var(--brand-primary)")}>
              {t("action.edit", "Edit")}
            </button>
            <button type="button" disabled={!hasPermission("section:companies:full")} onClick={onDelete} style={textBtn("var(--err)")}>
              {t("action.delete", "Delete")}
            </button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {a.usableForDelivery && !a.defaultDelivery && (
              <button type="button" onClick={() => onSetDefault("delivery")} style={textBtn("var(--ink-3)")}>
                {t("company.addresses.setDefaultDelivery", "Set default delivery")}
              </button>
            )}
            {a.usableForBilling && !a.defaultBilling && (
              <button type="button" onClick={() => onSetDefault("billing")} style={textBtn("var(--ink-3)")}>
                {t("company.addresses.setDefaultBilling", "Set default billing")}
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

/** Add/edit dialog for an address-book entry (internal management path). */
function AddressFormDialog({
  initial, onClose, onSave,
}: {
  initial: CompanyAddress | null;
  onClose: () => void;
  onSave: (body: CompanyAddressBody) => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [label, setLabel] = useState(initial?.label ?? "");
  const [recipientName, setRecipientName] = useState(initial?.recipientName ?? "");
  const [addressLine1, setAddressLine1] = useState(initial?.addressLine1 ?? "");
  const [addressLine2, setAddressLine2] = useState(initial?.addressLine2 ?? "");
  const [city, setCity] = useState(initial?.city ?? "");
  const [postalCode, setPostalCode] = useState(initial?.postalCode ?? "");
  const [country, setCountry] = useState(initial?.country ?? "");
  const [vatNumber, setVatNumber] = useState(initial?.vatNumber ?? "");
  const [usableForDelivery, setUsableForDelivery] = useState(initial?.usableForDelivery ?? true);
  const [usableForBilling, setUsableForBilling] = useState(initial?.usableForBilling ?? true);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!addressLine1.trim()) {
      toast.error(t("company.addresses.addressLine1Required", "Address line 1 is required."));
      return;
    }
    if (!usableForDelivery && !usableForBilling) {
      toast.error(t("company.addresses.purposeRequired", "Pick at least one purpose (delivery or billing)."));
      return;
    }
    setBusy(true);
    try {
      await onSave({
        label: label.trim() || null,
        recipientName: recipientName.trim() || null,
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim() || null,
        city: city.trim() || null,
        postalCode: postalCode.trim() || null,
        country: country || null,
        vatNumber: vatNumber.trim() || null,
        usableForDelivery,
        usableForBilling,
      });
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" style={overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal-panel" style={dialog} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>
          {initial ? t("company.addresses.editTitle", "Edit address") : t("company.addresses.addTitle", "Add address")}
        </h3>
        <AddressFormFields
          label={label} setLabel={setLabel}
          recipientName={recipientName} setRecipientName={setRecipientName}
          addressLine1={addressLine1} setAddressLine1={setAddressLine1}
          addressLine2={addressLine2} setAddressLine2={setAddressLine2}
          city={city} setCity={setCity}
          postalCode={postalCode} setPostalCode={setPostalCode}
          country={country} setCountry={setCountry}
          vatNumber={vatNumber} setVatNumber={setVatNumber}
          usableForDelivery={usableForDelivery} setUsableForDelivery={setUsableForDelivery}
          usableForBilling={usableForBilling} setUsableForBilling={setUsableForBilling}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn" onClick={onClose}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "…" : t("action.save", "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function purposeChip(fg: string, bg: string): React.CSSProperties {
  return { fontSize: 10.5, fontWeight: 700, padding: "1px 7px", borderRadius: 999, background: bg, color: fg, letterSpacing: 0.3, whiteSpace: "nowrap" };
}
function textBtn(color: string): React.CSSProperties {
  return { background: "transparent", border: "none", color, fontSize: 12, cursor: "pointer", padding: 0, whiteSpace: "nowrap" };
}
const overlay: React.CSSProperties = {
  position: "fixed", inset: 0, background: "var(--overlay)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50,
};
const dialog: React.CSSProperties = {
  width: "100%", maxWidth: 520, maxHeight: "85vh", overflowY: "auto",
  background: "var(--surface)", padding: 24, borderRadius: 12,
  boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14,
};

function EmptyHint({ icon, text, hint }: { icon: string; text: string; hint?: string }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "22px 12px", gap: 4,
      background: "var(--surface-2)", borderRadius: 8,
      border: "1px dashed var(--border)",
    }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div style={{ fontSize: 13, color: "var(--ink-2)", fontWeight: 500 }}>{text}</div>
      {hint && <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>{hint}</div>}
    </div>
  );
}

function CoverageRow({
  entry, canRemove, onRemove,
}: {
  entry: SalesCoverageEntry;
  canRemove: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const isScope = entry.origin === "SCOPE";
  return (
    <li style={row}>
      <span style={{ width: 28, height: 28, borderRadius: 999, background: "var(--surface-2)",
                     display: "grid", placeItems: "center", color: "var(--ink-2)",
                     fontSize: 11, fontWeight: 700 }}>
        {initials(entry.name)}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {entry.name}
          {entry.primary && (
            <span style={primaryChip} title={t("admin.companyDetail.otkupAssignments.primaryTitle", "Primary otkup officer")}>
              {"★ "}{t("admin.companyDetail.coverage.primary", "PRIMARY")}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{entry.email}</div>
      </div>
      <span
        style={originChip(isScope)}
        title={isScope
          ? t("admin.companyDetail.coverage.viaScopeTitle", { country: entry.countryCode ?? "", defaultValue: "Via scope · country {{country}}" })
          : t("admin.companyDetail.coverage.directTitle", "Direct per-company assignment")}
      >
        {isScope
          ? <>{t("admin.companyDetail.coverage.viaScope", "via scope")} {entry.countryCode ? `· ${flagEmoji(entry.countryCode)} ${entry.countryCode}` : ""}</>
          : t("admin.companyDetail.coverage.direct", "direct")}
      </span>
      {canRemove && (
        <button type="button" onClick={onRemove} style={removeLink}>{t("admin.companyDetail.action.remove", "Remove")}</button>
      )}
    </li>
  );
}

function OtkupAssignmentRow({
  entry, onRemove,
}: {
  entry: OtkupAssignment;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li style={row}>
      <span style={{ width: 28, height: 28, borderRadius: 999, background: "var(--surface-2)",
                     display: "grid", placeItems: "center", color: "var(--ink-2)",
                     fontSize: 11, fontWeight: 700 }}>
        {initials(entry.otkupUserFullName)}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {entry.otkupUserFullName}
          {entry.primary && (
            <span style={primaryChip} title={t("admin.companyDetail.otkupAssignments.primaryTitle", "Primary otkup officer")}>
              {"★ "}{t("admin.companyDetail.coverage.primary", "PRIMARY")}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{entry.otkupUserEmail}</div>
      </div>
      <button type="button" onClick={onRemove} style={removeLink}>{t("admin.companyDetail.action.remove", "Remove")}</button>
    </li>
  );
}

function DetailGrid({ rows }: { rows: [string, string][] }) {
  return (
    <dl style={{ display: "grid", gridTemplateColumns: "minmax(120px, max-content) 1fr", gap: "6px 16px", margin: 0, fontSize: 13 }}>
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: "contents" }}>
          <dt style={{ color: "var(--ink-3)" }}>{label}</dt>
          <dd style={{ margin: 0, color: "var(--ink-2)" }}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function KpiTile({
  label, value, accent, bg, hint,
}: {
  label: string; value: number | string; accent: string; bg: string; hint?: string;
}) {
  return (
    <div className="card" style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: accent, boxShadow: `0 0 0 4px ${bg}` }} />
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{hint}</div>}
    </div>
  );
}

function RollupSummary({
  total, groups,
}: {
  total: number;
  groups: { label: string; count: number; accent: string }[];
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
      <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
        <b style={{ color: "var(--ink)" }}>{total}</b> {t("admin.companyDetail.rollup.total", "total")}
      </span>
      {groups.map((g) => (
        <span key={g.label} style={{ fontSize: 12, color: "var(--ink-3)", display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: g.count > 0 ? g.accent : "var(--border)" }} />
          {g.label} <b style={{ color: g.count > 0 ? "var(--ink)" : "var(--ink-4)" }}>{g.count}</b>
        </span>
      ))}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>{children}</p>;
}

function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function fmtDate(iso: string, locale?: string): string {
  return new Date(iso).toLocaleDateString(intlLocale(locale), { day: "numeric", month: "short", year: "numeric" });
}


const tabBar: React.CSSProperties = {
  display: "inline-flex", padding: 4, gap: 4, background: "var(--surface-2)",
  border: "1px solid var(--border)", borderRadius: 999, width: "fit-content",
};
function tabBtn(active: boolean): React.CSSProperties {
  return {
    padding: "6px 18px", borderRadius: 999, fontSize: 13, fontWeight: 600,
    border: "none", cursor: "pointer", transition: "background .12s",
    color: active ? "var(--on-primary)" : "var(--ink-2)",
    background: active ? "var(--brand-primary)" : "transparent",
  };
}
const kpiGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 };
const twoCol: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 14 };
const list: React.CSSProperties = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 };
const row: React.CSSProperties = { display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8 };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, color: "var(--ink)" };
const actionLink: React.CSSProperties = { fontSize: 12, color: "var(--brand-primary)", textDecoration: "none", fontWeight: 600 };
const neutralChip: React.CSSProperties = { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "var(--surface-2)", color: "var(--ink-2)", whiteSpace: "nowrap" };
const primaryChip: React.CSSProperties = { marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: "var(--warn-bg)", color: "var(--warn)", letterSpacing: 0.4 };
function originChip(isScope: boolean): React.CSSProperties {
  return isScope
    ? { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999,
        background: "var(--tone-violet-bg)", color: "var(--tone-violet)", whiteSpace: "nowrap" }
    : { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999,
        background: "var(--info-bg)", color: "var(--info)", whiteSpace: "nowrap" };
}
const removeLink: React.CSSProperties = { background: "transparent", border: "none", color: "var(--err)", fontSize: 12, cursor: "pointer" };
const crumbLink: React.CSSProperties = { color: "var(--ink-3)", textDecoration: "none" };
const crumbCurrent: React.CSSProperties = { color: "var(--ink-2)", fontWeight: 600 };
